// pages/master/role/VehicleManage.jsx
'use client';

import { useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
  TablePagination
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SearchIcon from '@mui/icons-material/Search';

import FormDialog from '../../../../components/FormDialog';
import { useNotifier } from '../../../../components/Notifier';
import { useLanguage } from '../../../../utils/i18n';

import VehicleForRoleDialog from './VehicleForRoleDialog';

const VehicleManage = ({ roleName, dataVehicleList, dataSearchVehicle, onRefresh }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  // --- MAPPING DATA AWAL ---
  const vehicleIds = useMemo(() => {
    let ids = [];
    if (!dataVehicleList) return [];
    if (Array.isArray(dataVehicleList) && dataVehicleList.length > 0) {
      ids = dataVehicleList[0].vehicle_id || [];
    } else {
      ids = dataVehicleList.vehicle_id || [];
    }
    return [...new Set(ids)];
  }, [dataVehicleList]);

  const assignedVehicles = useMemo(() => {
    if (!vehicleIds || vehicleIds.length === 0) return [];
    if (!dataSearchVehicle || dataSearchVehicle.length === 0) return [];

    return vehicleIds.reduce((result, id) => {
      const match = dataSearchVehicle.find((v) => String(v.id) === String(id));

      if (match) {
        const plate = match.license_plate || '-';
        const name = match.name || '-';

        const displayLabel = plate === name ? plate : `${plate} - ${name}`;

        result.push({
          id,
          label: displayLabel,
          companyName: match?.join?.company_name || ''
        });
      }

      return result;
    }, []);
  }, [vehicleIds, dataSearchVehicle]);

  // --- STATE PENCARIAN & PAGINATION ---
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 1. Filter Data berdasarkan Pencarian
  const filteredVehicles = useMemo(() => {
    if (!searchKeyword) return assignedVehicles;
    const lowerKeyword = searchKeyword.toLowerCase();

    return assignedVehicles.filter(
      (v) =>
        (v.label && v.label.toLowerCase().includes(lowerKeyword)) ||
        (v.companyName && v.companyName.toLowerCase().includes(lowerKeyword)) ||
        String(v.id).includes(lowerKeyword)
    );
  }, [assignedVehicles, searchKeyword]);

  // 2. Potong Data berdasarkan Pagination (Client-Side)
  const paginatedVehicles = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredVehicles.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredVehicles, page, rowsPerPage]);

  // --- HANDLER PENCARIAN & PAGINATION ---
  const handleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
    setPage(0); // Reset ke halaman pertama setiap kali mencari
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // --- MODAL & SAVE LOGIC ---
  const vehicleDialogRef = useRef(null);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [vehicleDialogDirty, setVehicleDialogDirty] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);

  const handleEditClick = () => setVehicleDialogOpen(true);
  const handleVehicleDialogClose = () => setVehicleDialogOpen(false);

  const handleDialogVehicleAccessSave = async () => {
    if (!vehicleDialogRef.current || typeof vehicleDialogRef.current.save !== 'function') return;

    setVehicleSaving(true);
    try {
      await vehicleDialogRef.current.save();
      notify({ message: t('notify.saved', 'Saved successfully'), severity: 'success' });
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
      setVehicleDialogOpen(false);
    } catch (err) {
      console.error('Save vehicle access error', err);
      notify({ message: err?.message ?? t('notify.failedSave', 'Failed to save data'), severity: 'error' });
    } finally {
      setVehicleSaving(false);
      setVehicleDialogDirty(false);
    }
  };

  return (
    <Box>
      {/* HEADER & ACTIONS */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" color="primary" fontWeight="bold">
          {t('masterRole.assignedVehicles', 'Assigned Vehicles')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Input Pencarian */}
          <TextField
            size="small"
            placeholder={t('masterRole.selectVehiclePlaceholder', 'Search or select vehicle')}
            value={searchKeyword}
            onChange={handleSearchChange}
            sx={{ width: 300, bgcolor: '#fff' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          <Button
            variant="contained"
            color="info"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
            disableElevation
            sx={{ textTransform: 'none', height: 40 }}
          >
            {t('masterRole.editVehicleAccess', 'Edit Vehicle Access')}
          </Button>
        </Box>
      </Box>

      {/* CONTAINER LIST DENGAN SCROLL & PAGINATION */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Bagian List yang bisa di-scroll (maksimal ~60% tinggi layar agar tidak luber) */}
        <Box sx={{ maxHeight: 'calc(110vh - 350px)', overflowY: 'auto' }}>
          {paginatedVehicles.length > 0 ? (
            <List sx={{ p: 0 }}>
              {paginatedVehicles.map((vehicle, index) => (
                <Box key={vehicle.id}>
                  <ListItem sx={{ py: 1.5, gap: 2 }}>
                    <ListItemIcon>
                      <DirectionsCarIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={vehicle.label}
                      secondary={vehicle.companyName ? `Company: ${vehicle.companyName}` : `Vehicle ID: ${vehicle.id}`}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                  {index < paginatedVehicles.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <DirectionsCarIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
              <Typography>
                {searchKeyword
                  ? t('general.noDataMatch', 'No data matches the search.')
                  : t('masterRole.noVehicleSelected', 'No vehicles selected yet.')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Pagination Component */}
        <Divider />
        <TablePagination
          component="div"
          count={filteredVehicles.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('general.rowsPerPage', 'Rows per page:')}
        />
      </Paper>

      {/* MODAL EDIT VEHICLE ACCESS */}
      <FormDialog
        open={vehicleDialogOpen}
        onClose={handleVehicleDialogClose}
        title={`${t('masterRole.manageVehicle', 'Manage Vehicle')} | Role: ${roleName || '-'}`}
        fullScreenMode={true}
        fullHeight={true}
        actions={
          <>
            <Button
              variant="contained"
              disabled={!vehicleDialogDirty || vehicleSaving}
              onClick={handleDialogVehicleAccessSave}
              sx={{ backgroundColor: '#0072DA' }}
            >
              {vehicleSaving ? t('general.processing', 'Processing...') : t('general.submit', 'Submit')}
            </Button>
            <Button onClick={handleVehicleDialogClose} disableRipple disableElevation variant="outlined">
              {t('general.cancel', 'Cancel')}
            </Button>
          </>
        }
      >
        <VehicleForRoleDialog
          ref={vehicleDialogRef}
          roleName={roleName}
          dataVehicleList={dataVehicleList}
          dataSearchVehicle={dataSearchVehicle}
          onDirtyChange={setVehicleDialogDirty}
        />
      </FormDialog>
    </Box>
  );
};

export default VehicleManage;
